import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DocumentService } from 'app/services/document';
import { holdReleaseSwitch } from 'app/platform/release/release';
import {
  ArchivedDocumentRef,
  DocumentArchiveReason,
} from 'app/interfaces/responses/document';
import {
  setDocumentIDsInLocalStorage,
  getDocumentIDsFromLocalStorage,
  setDocumentGroupsInLocalStorage,
  getDocumentGroupsFromLocalStorage,
} from 'app/utils/localStorageHandler/document';

export interface DocumentUploadStage {
  id: string;
  title: string;
  isCompleted: boolean;
  isActive: boolean;
}

export interface DocumentStatusItem {
  id: string;
  title: string;
  status:
    | 'SUCCESS'
    | 'PENDING'
    | 'FAILED'
    | 'ERROR'
    | 'NOT_APPLICABLE'
    | 'NOT_FOUND';
  transactionId?: string;
  transactionLabel?: string;
  type?: string;
  // Set on a split child: the bundle document it was cut from.
  parentDocumentId?: string;
  // Uploaded file this doc came from — the bundle's file for split children.
  sourceFileName?: string;
  // 1-indexed page span within the bundle; only set for split children.
  sourcePageRange?: { start: number; end: number };
  // ISO timestamp of when this document version was created/uploaded.
  createdAt?: string;
  // True once the backend has finished evaluating archival for this doc.
  archivalResolved?: boolean;
  // Present if THIS doc was archived (replacedBy absent for auto-archive).
  archivedInfo?: {
    reason?: DocumentArchiveReason;
    replacedBy?: ArchivedDocumentRef;
  };
  // Present if THIS doc caused others to be archived.
  replacedDocuments?: Array<
    ArchivedDocumentRef & { reason: DocumentArchiveReason }
  >;
}

// Settled share of tracked documents. The upload phase has its own indeterminate bar, so this is the whole bar.
const extractionPercent = (resolved: number, total: number) =>
  total > 0 ? Math.round((resolved / total) * 100) : 0;

// Extraction is over for this upload. SUCCESS and FAILED settle a step later than the rest,
// because only they wait on the backend resolving archival.
// Prod extractions finish within minutes (p90 150s from upload), so a document still pending
// this long is stuck server-side: the viewer unlocks it and Processing stops listing it.
export const EXTRACTION_STALL_MS = 15 * 60 * 1000;

export const isUploadSettled = (status: DocumentStatusItem) => {
  if (status.status === 'PENDING') return false;
  if (status.status === 'SUCCESS' || status.status === 'FAILED') {
    return !!status.archivalResolved;
  }
  return true;
};

const MISSING_UPLOAD_RETRY_MS = 3000;
const MISSING_UPLOAD_MAX_RETRIES = 3;

// A document joins the transaction only when its extraction ends, so refetch as each one
// settles rather than once at the end: its row moves from Processing into its folder/list.
// The settle signal is written right after the attach and the refetch reads a secondary, so
// that refetch can still come back without the document and nothing would ask again: while a
// settled upload for `listed.transactionID` is absent from `listed.documents`, refetch a few
// more times. Reads `refetch` through a ref so callers don't need a stable reference to depend on.
export const useRefetchOnUploadSettle = (
  documentStatuses: DocumentStatusItem[],
  refetch: () => void,
  listed?: {
    transactionID: string | undefined;
    documents: Array<{ _id: string }> | undefined;
  }
) => {
  const settledCount = documentStatuses.filter(isUploadSettled).length;
  const prevSettledCount = useRef(settledCount);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (settledCount > prevSettledCount.current) refetchRef.current();
    prevSettledCount.current = settledCount;
  }, [settledCount]);

  const transactionID = listed?.transactionID;
  const documents = listed?.documents;
  const missingKey = useMemo(() => {
    if (!transactionID || !documents) return '';
    const listedIds = new Set(documents.map((doc) => doc._id));
    // Archived and unknown uploads never join the list (see useRecentUploads).
    return documentStatuses
      .filter(
        (status) =>
          status.transactionId === transactionID &&
          isUploadSettled(status) &&
          !status.archivedInfo &&
          status.status !== 'NOT_FOUND' &&
          !listedIds.has(status.id)
      )
      .map((status) => status.id)
      .sort()
      .join(',');
  }, [transactionID, documents, documentStatuses]);

  const retries = useRef(0);
  const retriedFor = useRef('');
  useEffect(() => {
    if (missingKey !== retriedFor.current) {
      retriedFor.current = missingKey;
      retries.current = 0;
    }
    if (!missingKey || retries.current >= MISSING_UPLOAD_MAX_RETRIES) return;
    const timer = setTimeout(() => {
      retries.current += 1;
      refetchRef.current();
    }, MISSING_UPLOAD_RETRY_MS);
    return () => clearTimeout(timer);
    // `documents` is the trigger: each refetch that still lacks the upload schedules the next one.
  }, [missingKey, documents]);
};

export interface DocumentGroup {
  transactionId: string;
  transactionLabel?: string;
  documentIDs: string[];
}

// Files handed to an upload request that hasn't returned yet. The POST classifies
// synchronously before responding (p50 8s, p90 27s), so for that whole window the
// batch token is the only handle we have — there is no document ID to track.
export interface PendingUpload {
  uploadId: string;
  transactionId: string;
  transactionLabel?: string;
  fileNames: string[];
}

export interface DocumentUploadBannerState {
  isVisible: boolean;
  isMinimized: boolean;
  currentStage: number;
  stages: DocumentUploadStage[];
  progress: number;
  isComplete: boolean;
  phase: 'upload' | 'extraction' | 'complete';
  isClassifying: boolean;
  documentIDs: string[];
  documentStatuses: DocumentStatusItem[];
  documentGroups: DocumentGroup[];
  // In-flight uploads, keyed by batch token because no document ID exists yet.
  pendingUploads: PendingUpload[];
  // Rejected by the backend before a document existed — distinct from
  // extractionProgress.failed, which counts documents that failed extraction.
  failedFiles: string[];
  extractionProgress: {
    total: number;
    remaining: number;
    successful: number;
    failed: number;
    skipped: number;
  };
}

type PhaseRef = 'idle' | 'upload' | 'extraction' | 'complete';

const UPLOAD_STAGES: DocumentUploadStage[] = [
  {
    id: 'upload',
    title: 'Classifying your documents',
    isCompleted: false,
    isActive: false,
  },
  {
    id: 'scan',
    title: 'Max is scanning your documents',
    isCompleted: false,
    isActive: false,
  },
];

const INITIAL_STATE: DocumentUploadBannerState = {
  isVisible: false,
  isMinimized: true,
  currentStage: 0,
  stages: [...UPLOAD_STAGES],
  progress: 0,
  isComplete: false,
  phase: 'upload',
  isClassifying: false,
  documentIDs: [],
  documentStatuses: [],
  documentGroups: [],
  pendingUploads: [],
  failedFiles: [],
  extractionProgress: {
    total: 0,
    remaining: 0,
    successful: 0,
    failed: 0,
    skipped: 0,
  },
};

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_DURATION_MS = 7.5 * 60 * 1000;
// Must outlast a slow upload request, which classifies before it responds (prod p99
// 141s, max seen 245s): a shorter guard drops the banner while the upload is still running.
const UPLOAD_PHASE_TIMEOUT_MS = 5 * 60 * 1000;
const MAX_CONSECUTIVE_ERRORS = 3;
// Safety cap: if a single poll doesn't complete in this window we bail and
// retry rather than hanging forever (e.g. axios request was silently cancelled
// by a sibling caller's dedupe interceptor).
const POLL_REQUEST_TIMEOUT_MS = 30 * 1000;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export const useDocumentUploadBanner = (onDocumentsComplete?: () => void) => {
  const [state, setState] = useState<DocumentUploadBannerState>(INITIAL_STATE);

  // --- Authoritative refs. State mirrors these for the UI. ---

  // Full set of document IDs currently being tracked across any number of
  // uploads/transactions. Polled in full each tick — no pruning.
  const trackedRef = useRef<string[]>([]);
  // Per-transaction grouping for UI labels.
  const groupsRef = useRef<DocumentGroup[]>([]);
  // Current phase. Drives startUpload/startExtraction routing and scheduled
  // auto-dismiss. 'idle' = banner not shown, no tracked docs.
  const phaseRef = useRef<PhaseRef>('idle');
  // Exactly one polling loop may run at a time.
  const pollingRef = useRef(false);
  // Ends the poll loop's current wait early; set only while it is waiting.
  const wakePollRef = useRef<(() => void) | null>(null);
  // Soft-cancel flag read by the polling loop to bail out early (dismiss,
  // timeout, or error burst).
  const cancelledRef = useRef(false);
  // Last-seen absolute success count across the tracked set. Used to fire
  // onDocumentsComplete only on a true delta (prevents per-tick spam now that
  // every poll covers the full tracked set).
  const lastSuccessCountRef = useRef(0);
  // Caller-supplied callback for "cycle finished" (set via startExtraction).
  const onCompleteRef = useRef<(() => void) | null>(null);
  // In-flight upload requests, keyed by a token rather than by transaction so two
  // concurrent uploads onto the same transaction can't clear each other's rows.
  const pendingRef = useRef<PendingUpload[]>([]);
  // Synchronous mirror of the visible document rows. Used so pollOnce can
  // reconcile against the latest statuses without waiting for React state
  // updates to flush, and can compute return values in the same tick.
  const statusesRef = useRef<DocumentStatusItem[]>([]);

  // --- Timers ---
  const uploadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Per-batch safety net: unlike uploadTimeoutRef this fires in any phase, so a caller
  // that never settles its request cannot strand filename rows.
  const batchTimersRef = useRef(new Map<string, NodeJS.Timeout>());
  const clearAllTimers = useCallback(() => {
    if (uploadTimeoutRef.current) {
      clearTimeout(uploadTimeoutRef.current);
      uploadTimeoutRef.current = null;
    }
    batchTimersRef.current.forEach((timer) => clearTimeout(timer));
    batchTimersRef.current.clear();
  }, []);

  /**
   * Tear everything down: dismissals, timeouts, errors, unmount. Leaves the
   * banner hidden and refs fresh so the next startUpload can begin cleanly.
   */
  const hardReset = useCallback(() => {
    cancelledRef.current = true;
    phaseRef.current = 'idle';
    trackedRef.current = [];
    groupsRef.current = [];
    statusesRef.current = [];
    pendingRef.current = [];
    lastSuccessCountRef.current = 0;
    onCompleteRef.current = null;
    clearAllTimers();
    setDocumentIDsInLocalStorage([]);
    setDocumentGroupsInLocalStorage([]);
    setState(INITIAL_STATE);
  }, [clearAllTimers]);

  /**
   * Build a pending placeholder row for an ID we're tracking but don't yet
   * have a response for (newly merged uploads, or gaps in a partial response).
   */
  const buildPendingPlaceholder = useCallback(
    (id: string, groups: DocumentGroup[]): DocumentStatusItem => {
      const group = groups.find((g) => g.documentIDs.includes(id));
      return {
        id,
        title: 'Processing...',
        status: 'PENDING',
        transactionId: group?.transactionId,
        transactionLabel: group?.transactionLabel,
      };
    },
    []
  );

  /**
   * Produce one DocumentStatusItem per tracked ID, in tracked-order. Priority:
   *  1. latest response entry (if the id is in this poll's response)
   *  2. previously observed entry (preserve last known non-pending status)
   *  3. a PENDING placeholder (newly merged IDs, etc.)
   */
  const reconcileStatuses = useCallback(
    (
      trackedIds: string[],
      responseData: Array<
        Pick<DocumentStatusItem, 'id' | 'title'> & {
          status: string;
          type?: string;
          parentDocumentId?: string;
          sourceFileName?: string;
          sourcePageRange?: DocumentStatusItem['sourcePageRange'];
          createdAt?: string;
          archivalResolved?: boolean;
          archivedInfo?: DocumentStatusItem['archivedInfo'];
          replacedDocuments?: DocumentStatusItem['replacedDocuments'];
        }
      >,
      prevStatuses: DocumentStatusItem[],
      groups: DocumentGroup[]
    ): DocumentStatusItem[] => {
      const responseMap = new Map(responseData.map((d) => [d.id, d]));
      const prevMap = new Map(prevStatuses.map((s) => [s.id, s]));

      return trackedIds.map((id) => {
        const r = responseMap.get(id);
        if (r) {
          const group = groups.find((g) => g.documentIDs.includes(id));
          return {
            id,
            title: r.title,
            status: r.status as DocumentStatusItem['status'],
            transactionId: group?.transactionId,
            transactionLabel: group?.transactionLabel,
            type: r.type,
            parentDocumentId: r.parentDocumentId,
            sourceFileName: r.sourceFileName,
            sourcePageRange: r.sourcePageRange,
            createdAt: r.createdAt,
            archivalResolved: r.archivalResolved,
            archivedInfo: r.archivedInfo,
            replacedDocuments: r.replacedDocuments,
          };
        }
        return prevMap.get(id) ?? buildPendingPlaceholder(id, groups);
      });
    },
    [buildPendingPlaceholder]
  );

  /**
   * Poll once for the given IDs and mirror the response into state. Does NOT
   * decide completion — the loop owns that, and only after also checking
   * whether new IDs were merged in mid-poll.
   */
  const pollOnce = useCallback(
    async (idsToPoll: string[]) => {
      // Race the request against a timeout so a silently-cancelled promise
      // can't stall the entire poll loop. Track the timer so we can clear it
      // on success instead of leaking a rejection that no-one awaits.
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const response = await Promise.race([
        new DocumentService().getDocumentExtractionStatus(idsToPoll),
        new Promise<never>((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error('poll request timed out')),
            POLL_REQUEST_TIMEOUT_MS
          );
        }),
      ]).finally(() => {
        if (timeoutId) clearTimeout(timeoutId);
      });
      const data = response.data;

      // Reconcile response against the full tracked set (which may include
      // IDs merged in after this poll was dispatched). Placeholder rows are
      // inserted for tracked IDs not yet covered by any response.
      const reconciled = reconcileStatuses(
        trackedRef.current,
        data,
        statusesRef.current,
        groupsRef.current
      );

      let successful = 0;
      let failed = 0;
      let skipped = 0;
      let pendingReconciled = 0;
      reconciled.forEach((s) => {
        if (s.status === 'SUCCESS') successful++;
        else if (s.status === 'PENDING') pendingReconciled++;
        else if (s.status === 'NOT_APPLICABLE') skipped++;
        else failed++;
      });

      // Completion is driven by what the API reports, not by placeholders.
      // If the backend silently drops an ID from a later response the
      // reconciled list will keep a placeholder forever — don't let that
      // stall the cycle. The runPollLoop's hasNewIDs check still prevents
      // finishing prematurely when a new upload merges in mid-poll.
      let responsePending = 0;
      data.forEach((s) => {
        if (s.status === 'PENDING') {
          responsePending++;
        } else if (
          (s.status === 'SUCCESS' || s.status === 'FAILED') &&
          s.archivalResolved !== true
        ) {
          // Extraction is terminal but the backend hasn't finished evaluating
          // archival yet. Keep polling so we don't stop before the
          // archived-docs notification is recorded (closes the backend race
          // where status flips terminal before archival is written). Only
          // SUCCESS/FAILED flow through that archival step — NOT_FOUND/ERROR/
          // NOT_APPLICABLE never set archivalResolved, so they aren't gated.
          responsePending++;
        }
      });

      // Still in extraction? If cancelled / finalized while this request was
      // in flight, discard the result rather than writing stale state.
      const applicable =
        phaseRef.current === 'extraction' && !cancelledRef.current;

      if (applicable) {
        statusesRef.current = reconciled;

        const total = reconciled.length;
        const resolved = successful + failed + skipped;
        const progress = extractionPercent(resolved, total);

        setState((prev) => ({
          ...prev,
          extractionProgress: {
            total,
            remaining: pendingReconciled,
            successful,
            failed,
            skipped,
          },
          documentIDs: trackedRef.current,
          documentStatuses: reconciled,
          documentGroups: groupsRef.current,
          progress,
        }));
      }

      // allResolved follows the API response. Placeholders for un-returned
      // IDs don't block completion (see note above).
      return {
        allResolved: applicable && responsePending === 0,
        successCount: successful,
      };
    },
    [reconcileStatuses]
  );

  /**
   * Mark the cycle as complete. Idempotent. Only the polling loop should call
   * this — it's the single completion trigger, whether reached by observing
   * all-resolved in the response or by the loop otherwise exiting with an
   * empty tracked set.
   */
  const finalizeComplete = useCallback(() => {
    setState((prev) => {
      if (prev.isComplete) return prev;
      // If this upload archived any documents, keep the banner expanded on
      // completion so the per-document archival notes are visible — otherwise
      // the notification stays hidden behind the minimized "Upload complete" bar.
      const hasArchival = prev.documentStatuses.some(
        (d) => d.archivedInfo || (d.replacedDocuments?.length ?? 0) > 0
      );
      return {
        ...prev,
        isVisible: true,
        isMinimized: !hasArchival,
        currentStage: UPLOAD_STAGES.length - 1,
        progress: 100,
        isComplete: true,
        phase: 'complete',
        extractionProgress: { ...prev.extractionProgress, remaining: 0 },
        stages: UPLOAD_STAGES.map((s) => ({
          ...s,
          isActive: false,
          isCompleted: true,
        })),
      };
    });
    phaseRef.current = 'complete';
    trackedRef.current = [];
    // Clear group memory so the next cycle starts with clean transaction
    // labelling — otherwise stale groups would inflate txCount in the UI.
    groupsRef.current = [];
    lastSuccessCountRef.current = 0;
    setDocumentIDsInLocalStorage([]);
    setDocumentGroupsInLocalStorage([]);
    try {
      onCompleteRef.current?.();
    } catch (e) {
      console.error('onComplete callback threw:', e);
    }
    onCompleteRef.current = null;
  }, []);

  /**
   * Single polling loop. Owns the extraction→complete transition.
   *
   * Termination:
   *  - genuine completion (no pending in response AND no new IDs merged mid-poll)
   *  - trackedRef becomes empty by any other path → loop stopping is itself a
   *    completion signal (provided the cycle wasn't cancelled)
   *  - timeout or error burst → hard reset
   *  - dismiss / external cancel → cancelledRef = true; loop exits without
   *    finalizing
   */
  const runPollLoop = useCallback(async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    cancelledRef.current = false;

    let loopStart = Date.now();
    let consecutiveErrors = 0;
    const waitForNextPoll = () =>
      new Promise<void>((resolve) => {
        const done = () => {
          clearTimeout(timer);
          wakePollRef.current = null;
          resolve();
        };
        const timer = setTimeout(done, POLL_INTERVAL_MS);
        wakePollRef.current = done;
      });

    try {
      while (!cancelledRef.current && trackedRef.current.length > 0) {
        if (Date.now() - loopStart >= MAX_POLL_DURATION_MS) {
          hardReset();
          return;
        }

        const snapshot = [...trackedRef.current];

        let result: { allResolved: boolean; successCount: number };
        try {
          result = await pollOnce(snapshot);
          consecutiveErrors = 0;
        } catch (err) {
          consecutiveErrors++;
          console.error(
            `Document poll error (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`,
            err
          );
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            hardReset();
            return;
          }
          await sleep(POLL_INTERVAL_MS);
          continue;
        }

        if (cancelledRef.current) break;

        // Delta-based onDocumentsComplete callback. Fires only when the
        // absolute success count grows. Wrapped so a caller throw can't
        // derail the poll loop.
        if (result.successCount > lastSuccessCountRef.current) {
          try {
            onDocumentsComplete?.();
          } catch (e) {
            console.error('onDocumentsComplete threw:', e);
          }
        }
        lastSuccessCountRef.current = result.successCount;

        // Detect IDs merged in while the poll was in flight.
        const snapshotSet = new Set(snapshot);
        const hasNewIDs = trackedRef.current.some((id) => !snapshotSet.has(id));

        if (result.allResolved && !hasNewIDs) {
          // A batch goes out as several requests. One still classifying will merge its
          // documents into this cycle when it returns, so hold the cycle open for it.
          if (pendingRef.current.length > 0) {
            await waitForNextPoll();
            continue;
          }
          // Genuine completion. Break the loop; finalize in the post-loop
          // block so there's exactly one completion path.
          break;
        }

        // New IDs merged — reset the loop budget (the new uploads deserve a
        // fresh timeout window) and skip the sleep so the user sees them sooner.
        if (hasNewIDs) {
          loopStart = Date.now();
          continue;
        }

        await waitForNextPoll();
      }

      // Single completion trigger: loop stopped (either all resolved, or
      // trackedRef drained externally) and we weren't cancelled.
      if (!cancelledRef.current && phaseRef.current === 'extraction') {
        finalizeComplete();
      }
    } finally {
      pollingRef.current = false;
    }
  }, [pollOnce, hardReset, finalizeComplete, onDocumentsComplete]);

  const ensurePollLoop = useCallback(() => {
    if (!pollingRef.current && trackedRef.current.length > 0) {
      runPollLoop();
    }
  }, [runPollLoop]);

  // Settles one batch, success or failure. The last batch out decides what remains: in the
  // upload phase nothing else is on screen, so the banner goes with it; in extraction or
  // complete the tracked documents keep it up.
  const resolveUpload = useCallback(
    (uploadId: string) => {
      clearTimeout(batchTimersRef.current.get(uploadId));
      batchTimersRef.current.delete(uploadId);
      pendingRef.current = pendingRef.current.filter(
        (p) => p.uploadId !== uploadId
      );
      const stillPending = pendingRef.current.length > 0;

      if (!stillPending && phaseRef.current === 'upload') {
        hardReset();
        return;
      }
      setState((prev) => ({
        ...prev,
        pendingUploads: pendingRef.current,
        // Only ever turns classifying off: the 'complete' timer may have cleared it already.
        isClassifying: prev.isClassifying && stillPending,
      }));
    },
    [hardReset]
  );

  /**
   * Begin a new upload. Shows the banner with an indeterminate state while
   * files are uploading; `startExtraction` transitions into tracked polling.
   *
   * Behavior by current phase:
   *  - 'extraction' (poll loop active): overlay "Classifying new documents"
   *    without restarting anything.
   *  - 'complete' (done banner still visible): same.
   *  - 'upload' (a request is still in flight): keep its rows, re-arm the timer.
   *  - 'idle': fresh upload cycle guarded by UPLOAD_PHASE_TIMEOUT_MS.
   */
  const startUpload = useCallback(
    (
      transactionId?: string,
      transactionLabel?: string,
      fileNames?: string[]
    ) => {
      // Token identifies this batch so resolveUpload can clear exactly these rows.
      const uploadId = uuidv4();
      const trackFiles = () => {
        if (!transactionId || !fileNames?.length) return;
        pendingRef.current = [
          ...pendingRef.current,
          { uploadId, transactionId, transactionLabel, fileNames },
        ];
        batchTimersRef.current.set(
          uploadId,
          setTimeout(() => resolveUpload(uploadId), UPLOAD_PHASE_TIMEOUT_MS)
        );
        setState((prev) => ({ ...prev, pendingUploads: pendingRef.current }));
      };

      if (phaseRef.current === 'extraction') {
        trackFiles();
        setState((prev) => ({
          ...prev,
          isClassifying: true,
        }));
        return uploadId;
      }

      if (phaseRef.current === 'complete') {
        // Safety: if extraction never fires, only drop the classifying flag —
        // don't blow away the "All done!" banner.
        if (uploadTimeoutRef.current) clearTimeout(uploadTimeoutRef.current);
        uploadTimeoutRef.current = setTimeout(() => {
          uploadTimeoutRef.current = null;
          setState((prev) => ({ ...prev, isClassifying: false }));
        }, UPLOAD_PHASE_TIMEOUT_MS);
        trackFiles();
        setState((prev) => ({
          ...prev,
          isClassifying: true,
        }));
        return uploadId;
      }

      const armUploadTimeout = () => {
        if (uploadTimeoutRef.current) clearTimeout(uploadTimeoutRef.current);
        uploadTimeoutRef.current = setTimeout(() => {
          if (phaseRef.current === 'upload') hardReset();
        }, UPLOAD_PHASE_TIMEOUT_MS);
      };

      // A second batch dropped while the first request is still in flight must not
      // wipe the first batch's rows; it just gets a fresh safety window.
      if (phaseRef.current === 'upload') {
        armUploadTimeout();
        trackFiles();
        return uploadId;
      }

      phaseRef.current = 'upload';
      cancelledRef.current = false;
      statusesRef.current = [];
      trackedRef.current = [];
      groupsRef.current = [];
      pendingRef.current = [];
      lastSuccessCountRef.current = 0;

      armUploadTimeout();

      setState((prev) => ({
        ...prev,
        isVisible: true,
        isMinimized: true,
        currentStage: 0,
        stages: UPLOAD_STAGES.map((stage, i) => ({
          ...stage,
          isActive: i === 0,
          isCompleted: false,
        })),
        progress: 0,
        isComplete: false,
        phase: 'upload',
        isClassifying: true,
        documentStatuses: [],
        documentIDs: [],
        documentGroups: [],
        pendingUploads: [],
        failedFiles: [],
        extractionProgress: {
          total: 0,
          remaining: 0,
          successful: 0,
          failed: 0,
          skipped: 0,
        },
      }));
      trackFiles();
      return uploadId;
    },
    [hardReset, resolveUpload]
  );

  // Merged, not replaced: a second upload while the banner is still up must not
  // erase the first upload's failures.
  const reportFailedFiles = useCallback((filenames: string[]) => {
    if (!filenames.length) return;
    setState((prev) => ({
      ...prev,
      failedFiles: [...new Set([...prev.failedFiles, ...filenames])],
    }));
  }, []);

  /**
   * Begin (or extend) the extraction phase for the given doc IDs. If a poll
   * loop is already running, the new IDs are merged into the tracked set and
   * the existing loop picks them up on its next tick.
   */
  const startExtraction = useCallback(
    (
      documentIDs: string[],
      transactionId?: string,
      transactionLabel?: string,
      onComplete?: () => void
    ) => {
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
        uploadTimeoutRef.current = null;
      }
      // Only clear the cancel flag if no poll loop is running. A live loop
      // owns cancelledRef; flipping it here would resurrect a loop that
      // hardReset had told to exit, and it would run against the new tracked
      // set with the old loopStart budget.
      if (!pollingRef.current) cancelledRef.current = false;

      if (onComplete) onCompleteRef.current = onComplete;

      // Empty extraction (e.g. all files were duplicates): keep the banner
      // attached to any in-flight cycle; otherwise dismiss it entirely —
      // don't fake success.
      if (documentIDs.length === 0) {
        if (trackedRef.current.length > 0 || pendingRef.current.length > 0) {
          setState((prev) => ({
            ...prev,
            isClassifying: pendingRef.current.length > 0,
          }));
        } else {
          hardReset();
        }
        onCompleteRef.current?.();
        onCompleteRef.current = null;
        return;
      }

      // Merge new IDs into the tracked set.
      const existing = trackedRef.current;
      const merged = [
        ...existing,
        ...documentIDs.filter((id) => !existing.includes(id)),
      ];
      trackedRef.current = merged;
      setDocumentIDsInLocalStorage(merged);

      // Merge group labels.
      if (transactionId) {
        const existingIdx = groupsRef.current.findIndex(
          (g) => g.transactionId === transactionId
        );
        if (existingIdx >= 0) {
          const updated = [...groupsRef.current];
          updated[existingIdx] = {
            ...updated[existingIdx],
            documentIDs: [
              ...new Set([...updated[existingIdx].documentIDs, ...documentIDs]),
            ],
          };
          groupsRef.current = updated;
        } else {
          groupsRef.current = [
            ...groupsRef.current,
            { transactionId, transactionLabel, documentIDs },
          ];
        }
        setDocumentGroupsInLocalStorage(groupsRef.current);
      }

      const cameFromComplete = phaseRef.current === 'complete';
      if (cameFromComplete) lastSuccessCountRef.current = 0;
      phaseRef.current = 'extraction';

      // Seed placeholder rows immediately so newly merged IDs appear in the
      // list without waiting for the next poll. Fresh cycle after 'complete'
      // must not carry forward the prior cycle's rows.
      const prevStatuses = cameFromComplete ? [] : statusesRef.current;
      statusesRef.current = reconcileStatuses(
        merged,
        [],
        prevStatuses,
        groupsRef.current
      );
      const reconciledNow = statusesRef.current;
      let seededSuccessful = 0;
      let seededFailed = 0;
      let seededSkipped = 0;
      let seededPending = 0;
      reconciledNow.forEach((s) => {
        if (s.status === 'SUCCESS') seededSuccessful++;
        else if (s.status === 'PENDING') seededPending++;
        else if (s.status === 'NOT_APPLICABLE') seededSkipped++;
        else seededFailed++;
      });

      setState((prev) => {
        const total = reconciledNow.length;
        const resolved = seededSuccessful + seededFailed + seededSkipped;
        const progress = extractionPercent(resolved, total);

        return {
          ...prev,
          isVisible: true,
          phase: 'extraction',
          // A batch goes out as several requests, so others may still be classifying.
          isClassifying: pendingRef.current.length > 0,
          isComplete: false,
          currentStage: 1,
          // Reflect the new merged total — if more docs were added mid-cycle
          // the bar should regress to match reality, not pin at its prior max.
          progress,
          documentIDs: merged,
          documentGroups: groupsRef.current,
          documentStatuses: reconciledNow,
          extractionProgress: {
            total,
            remaining: seededPending,
            successful: seededSuccessful,
            failed: seededFailed,
            skipped: seededSkipped,
          },
          stages: UPLOAD_STAGES.map((s, i) => ({
            ...s,
            isActive: i === 0,
            isCompleted: false,
          })),
        };
      });

      ensurePollLoop();
      // Poll now rather than at the next tick, so the new rows get their real names at once.
      wakePollRef.current?.();
    },
    [hardReset, ensurePollLoop, reconcileStatuses]
  );

  const dismissBanner = useCallback(() => {
    hardReset();
  }, [hardReset]);

  const stopClassifying = useCallback(() => {
    if (phaseRef.current === 'extraction' && trackedRef.current.length > 0) {
      setState((prev) => ({ ...prev, isClassifying: false }));
    } else {
      hardReset();
    }
  }, [hardReset]);

  const stopUpload = useCallback(() => {
    hardReset();
  }, [hardReset]);

  const toggleMinimize = useCallback(
    () => setState((prev) => ({ ...prev, isMinimized: !prev.isMinimized })),
    []
  );

  // Cleanup on unmount.
  useEffect(() => clearAllTimers, [clearAllTimers]);

  // A release switch reloads the tab; mid-upload that aborts the POST and drops batches with no document ID yet.
  useEffect(
    () =>
      holdReleaseSwitch(
        () => phaseRef.current === 'upload' || pendingRef.current.length > 0
      ),
    []
  );

  // Resume extraction from localStorage on mount. Just drop the IDs into the
  // tracked set and start the loop — the first poll will produce accurate UI.
  useEffect(() => {
    const storedJson = getDocumentIDsFromLocalStorage();
    if (!storedJson) return;
    let stored: string[];
    try {
      stored = JSON.parse(storedJson);
    } catch {
      setDocumentIDsInLocalStorage([]);
      return;
    }
    if (!Array.isArray(stored) || stored.length === 0) return;

    trackedRef.current = stored;
    const storedGroups = getDocumentGroupsFromLocalStorage();
    if (storedGroups && storedGroups.length > 0) {
      groupsRef.current = storedGroups;
    }
    phaseRef.current = 'extraction';
    // Seed placeholder rows so the banner has something to show until the
    // first poll response comes back.
    statusesRef.current = reconcileStatuses(stored, [], [], groupsRef.current);
    setState((prev) => ({
      ...prev,
      isVisible: true,
      phase: 'extraction',
      currentStage: 1,
      progress: 0,
      documentIDs: stored,
      documentGroups: groupsRef.current,
      documentStatuses: statusesRef.current,
      extractionProgress: {
        total: stored.length,
        remaining: stored.length,
        successful: 0,
        failed: 0,
        skipped: 0,
      },
    }));
    ensurePollLoop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ...state,
    startUpload,
    resolveUpload,
    startExtraction,
    reportFailedFiles,
    stopClassifying,
    dismissBanner,
    stopUpload,
    toggleMinimize,
  };
};
